// Shell der Hof-App.
//
// Drei Wege durch die App, und jeder hat seinen Zweck:
//
//   • Der STARTBILDSCHIRM mit den Kacheln — der Kompass, und die Heimat ALLER
//     Seiten, die einen Mitarbeiter angehen. Für jeden sichtbar.
//   • Die SEITENLEISTE (drei Striche oben links) — die VERWALTUNG. Nur für
//     Admins; wer keiner ist, sieht auch die drei Striche nicht.
//   • Die UNTERE LEISTE — die Handvoll Module, die man jeden Tag braucht.
//     Wer die sind, entscheidet jeder selbst, indem er eine Kachel vom
//     Startbildschirm hierher zieht. Wir raten nicht.
//
// Die untere Leiste ist ein Geschwister von <main>, kein Overlay. Dadurch
// schrumpft der Inhaltsbereich von selbst und nichts verschwindet dahinter —
// ein `position: fixed` bräuchte auf jeder einzelnen Seite ein passendes
// Bottom-Padding, und genau eine Seite vergisst man immer.
//
// Der PinDragProvider klammert BEIDE: Kachelfeld und Leiste sind Geschwister und
// müssten sonst raten, was der jeweils andere gerade tut.

import { useMemo, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/stores/authStore";
import { Sidebar } from "@/modules/home/Sidebar";
import { SidebarContext } from "@/modules/home/SidebarContext";
import { PinDragProvider } from "@/modules/home/PinDrag";
import { BottomBar } from "@/modules/home/BottomBar";
import HomePage from "@/modules/home/HomePage";
import CustomerListPage from "./CustomerListPage";
import CustomerEditPage from "./CustomerEditPage";
import TourPage from "@/modules/tour/TourPage";
import { TourStreifen } from "@/modules/tour/TourStreifen";
import NewsPage from "@/modules/news/NewsPage";
import QsPage from "@/modules/qs/QsPage";
import ZeitPage from "@/modules/zeit/ZeitPage";
import ProfilPage from "@/modules/profil/ProfilPage";
import ProduktePage from "@/modules/produkte/ProduktePage";
import AdminKassePage from "@/modules/admin/AdminKassePage";
import BankPage from "@/modules/bank/BankPage";
import AdminZeitenPage from "@/modules/admin/AdminZeitenPage";
import AdminTeamPage from "@/modules/admin/AdminTeamPage";

/** Admin-Seiten sind auch per Direktlink dicht — ein verstecktes Menü ist keine Sicherheit. */
function NurAdmin({ children }: { children: React.ReactNode }) {
  const role = useAuth((s) => s.role);
  if (role !== "admin") return <Navigate to="/crm" replace />;
  return <>{children}</>;
}

export default function CrmShell() {
  const [menuOffen, setMenuOffen] = useState(false);
  const sidebar = useMemo(() => ({ oeffnen: () => setMenuOffen(true) }), []);

  return (
    <SidebarContext.Provider value={sidebar}>
    <PinDragProvider>
    {/* Höhe = voller Bildschirm, minus einer offenen Tastatur.
        —
        `100%` folgt der Höhen-Kette der Vorfahren (body → #root → html). Die
        WURZEL dieser Kette (html) steht in index.css in der installierten App
        bewusst auf `100vh`, weil `100%`/`100dvh` dort den echten Bildschirm um
        die obere Notch-Zone zu kurz melden. `100%` erbt damit die richtige,
        volle Höhe; die untere Leiste sitzt bündig am Rand.
        —
        `--tastatur` ist nur bei einer wirklich offenen Tastatur > 0 (siehe
        native-gefuehl.ts) — dann schrumpft das Layout, damit der Speichern-Knopf
        nicht dahinter verschwindet. Ohne Tastatur ist es 0, also volle Höhe. */}
    <div
      style={{ height: "calc(100% - var(--tastatur, 0px))" }}
      className="flex flex-col overflow-hidden bg-ink-950 pt-[env(safe-area-inset-top)] text-ink-100 antialiased"
    >
      <main className="min-h-0 flex-1">
        <Routes>
          <Route index element={<HomePage />} />

          <Route path="news" element={<NewsPage />} />
          <Route path="tour" element={<TourPage />} />
          <Route path="qs" element={<QsPage />} />
          <Route path="zeit" element={<ZeitPage />} />

          <Route path="profil" element={<ProfilPage />} />

          {/* Produkte sieht jeder im Betrieb. Anlegen und Ändern hängt am Recht
              "produkte.anlegen" — durchgesetzt in der Datenbank, nicht hier. */}
          <Route path="produkte" element={<ProduktePage />} />
          {/* Der alte Admin-Pfad ist noch in Lesezeichen und in der Historie. */}
          <Route path="admin/produkte" element={<Navigate to="/crm/produkte" replace />} />

          <Route path="kunden" element={<CustomerListPage />} />
          <Route path="neu" element={<CustomerEditPage />} />

          <Route
            path="admin/kasse"
            element={
              <NurAdmin>
                <AdminKassePage />
              </NurAdmin>
            }
          />
          {/* Bank liegt unter /admin — die Umsätze sind per RLS auf admin/buero
              beschränkt, ein Menüeintrag für andere zeigte nur eine leere Seite. */}
          <Route
            path="admin/bank"
            element={
              <NurAdmin>
                <BankPage />
              </NurAdmin>
            }
          />
          <Route
            path="admin/zeiten"
            element={
              <NurAdmin>
                <AdminZeitenPage />
              </NurAdmin>
            }
          />
          <Route
            path="admin/team"
            element={
              <NurAdmin>
                <AdminTeamPage />
              </NurAdmin>
            }
          />

          {/* Muss NACH den festen Pfaden stehen, sonst schluckt :id sie alle. */}
          <Route path=":id" element={<CustomerEditPage />} />
        </Routes>
      </main>

      {/* Läuft gerade eine Tour, hängt der Weg zurück in sie über der Leiste —
          auf jeder Seite, einen Tipp entfernt. Geschwister von <main>, wie die
          Leiste selbst: So schrumpft der Inhalt von allein und verschwindet
          nichts dahinter. */}
      <TourStreifen />
      <BottomBar />
      <Sidebar open={menuOffen} onClose={() => setMenuOffen(false)} />
    </div>
    </PinDragProvider>
    </SidebarContext.Provider>
  );
}
